/***
 * Created on 2025/7/22
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React, {useReducer, useRef} from 'react';
import ModalComponent from "../common/ModalComonent/ModalComponent";
import {useTranslation} from "react-i18next";
import TableComponent from "../common/TableComponent/TableComponent";
import {dataCellLabelToDataGridArea} from "../../service/utils";
import useToggle from "../../hook/ToggleHook";
import {useMutation, useQuery} from "@tanstack/react-query";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useParams} from "react-router-dom";
import {useAlertContext} from "../../context/AlertContext";

const SHARE_ACCESS_PERMISSIONS = {
    view: '@Can view@',
    edit: '@Can edit@',
    noaccess: '@No Access@'
}

const reducerHandler = (state, action) => {
    switch (action.type) {
        case 'update_data':
            return {
                ...state,
                // user_id: state?.user_id,
                data: action.payload
            }
        case 'update_permission':
            return {
                ...state,
                permission_list: {...state.permission_list, [action.payload.space_id]: action.payload.access}
            };
        default:
            return state;
    }
}

function ManageShareAccessEmptyList({t}) {
    return (
        <div className="flexbox-tbody">
            <div className="no-data-screen">
                <div className="no-data-info">
                    <div className="no-data-desc">
                        <div className="desc-title">{t('@Empty Shared Spaces@')}</div>
                        <div className="desc-msg">{}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ManageShareAccessRowComponent({table_head_fields, row_data, state, index, doRefresh, disPatcher, table_ui, isOwner, permissions, show_checkbox=false, translate:t}) {
    const {oid} = useParams();
    const [open, node] = useToggle();
    const [permission, setPermission] = React.useState(() => {
        if (!row_data?.accesses || row_data?.accesses.length === 0) {
            return 'noaccess'; // Default permission
        }
        else {
            // const access = row_data.accesses.find((access) => access.domain.data === oid && access.subject.kind === 'user');
            const access = row_data.accesses[0];
            if (!access) {
                return 'noaccess'; // Default permission if no access found
            }
            else {
                if (access.permissions.includes('write') || access.permissions.includes('write_obj')) {
                    return 'edit';
                }
                else {
                    return 'view';
                }
            }
        }
    });

    const onPermissionChange = (e, permission) => {
        // Handle permission change logic here
        const read = ['read', 'read_obj'];
        const write = ['write', 'write_obj'];
        const perm_set = {
            view: read,
            edit: [...read, ...write]
        }
        if (!row_data?.accesses || row_data?.accesses[0]?.subject?.data !== 'user') {
            const access = {
                op: permission === 'noaccess' ? "delete" : "set",
                region: row_data.region,
                subject: {
                    "data": state.user_id,
                    "kind": "user"
                },
                target: {data: row_data.id, kind: 'space'},
                space_name: row_data.name,
            }
            if (access.op === 'set') {
                access.permissions = perm_set[permission];
            }
            disPatcher({type: 'update_permission', payload: {space_id: row_data.id, access}});
        }
        setPermission(permission);
    };

    return (
        <div className="flexbox-trow">
            <div className={`tbody-cell ${table_head_fields[0].width_style}`} data-cell-label={t(table_head_fields[0].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[0].field_name)}>
                {row_data?.name}
            </div>
            <div className={`tbody-cell ${table_head_fields[1].width_style}`} data-cell-label={t(table_head_fields[1].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[1].field_name)}>
                {t(`@${row_data?.region}@`)}
            </div>
            <div className={`tbody-cell ${table_head_fields[2].width_style}`} data-cell-label={t(table_head_fields[2].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[2].field_name)}>
                <div className={`comp-select comp-select--tiny auto-width drop-adjustable drop-align-right ${row_data?.accesses?.[0]?.subject?.data === 'seated_org_owner' ? 'disabled' : ''}`}>
                    <div className="select-toggle" ref={node}>
                        <span className="select-label">{t(SHARE_ACCESS_PERMISSIONS[permission])}</span>
                    </div>
                    <div className={`comp-dropdown ${open}`}>
                        <ul className="comp-dropdown-menu">
                            <li className="section">
                                <div className="section-items selection" onClick={e=>onPermissionChange(e, 'view')}>{t(SHARE_ACCESS_PERMISSIONS['view'])}</div>
                            </li>
                            <li className="section">
                                <div className="section-items selection" onClick={e=>onPermissionChange(e, 'edit')}>{t(SHARE_ACCESS_PERMISSIONS['edit'])}</div>
                            </li>
                            { ( row_data?.accesses?.[0]?.subject?.kind !== 'role' && row_data?.accesses?.[1]?.subject?.kind !== 'role') &&
                                <li className="section">
                                    <div className="section-items selection" onClick={e=>onPermissionChange(e, 'noaccess')}>{t(SHARE_ACCESS_PERMISSIONS['noaccess'])}</div>
                                </li>
                            }
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );

}

function AccountListManageShareAccess({isOpen, onClose, onConfirm, row_data, doRefresh}) {
    const {t} = useTranslation();
    const {OrganizationAPI} = useOrganizationApi()
    const {oid} = useParams();
    const alert = useAlertContext();
    const modalRef = useRef();
    const currentUser = JSON.parse(window.localStorage.getItem('currentUser'));

    const table_head_fields = [
        {field_name: t('@Spaces folders@'), isSortable: true, width_style: '!w-40', field_id: 'name', default_sort: true, default_sort_order: ''},
        {field_name: t('@region@'), isSortable: true, width_style: '!w-30', field_id: 'region'},
        {field_name: t('@Permissions@'), isSortable: false, width_style: '!w-30', field_id: 'permissions'}
    ];

    const {data, refetch} = useQuery({
        queryKey: ['manageShareAccess', row_data?.user_id],
        queryFn: async () => {
            if (!row_data?.user_id) return [];
            // Fetch the shared folder access data for the user
            const response = await OrganizationAPI.q2.get(`org_storage/${oid}/shared_space`, {user_id: row_data?.user_id});
            dispatch({type: 'update_data', payload: response.result});
            return response.result;
        },
    });

    const accessMutation = useMutation({
        mutationFn: async (accessData) => await OrganizationAPI.q2.patch(`access`, {org_id: oid}, accessData),
        onSuccess: async (data, variables) => {
            // Handle success, e.g., show a success message or refresh the data
            // doRefresh();
            const activity_data = {
                organization_id: oid,
                user_id: currentUser.user_id,
                event: 27,
                category: 6,
                meta_data: {data: variables, qid: row_data?.email}
            }
            await OrganizationAPI.put('organization_activity',null, activity_data);
            alert(true, 'success', 'ok', t('@Access permissions for {{display_name}} have been updated.@', 10*1000, {display_name: row_data?.display_name}));
            refetch();
            onClose();
        },
        onError: (error) => {
            // Handle error, e.g., show an error message
            console.error('Error updating access:', error);
            modalRef.current.alert(true, 'error', error?.code, 'Unable to change permission for {{share_space_name}}');
        }
    });

    const handleAccessChange = async () => {
        // Prepare the access data to be sent to the API
        let accessData = Object.values(state?.permission_list || {});
        console.log('Access Data:', accessData);

        // Call the mutation to update the access
        await accessMutation.mutateAsync(accessData);
    }

    const reducerInitializer = () => {
        return {
            user_id: row_data?.user_id,
            qid: row_data?.email,
            data: data || [],
            permission: 'noaccess' // Default permission
        };
    }

    const [state, dispatch] = useReducer(reducerHandler, {}, reducerInitializer);

    return (
        <ModalComponent isOpen={isOpen} onClose={onClose} title={t('@Manage Shared Folder Access for {{display_name}}@', {display_name: row_data?.display_name})} size="large" okText={t('@Apply@')} onSubmit={handleAccessChange} ref={modalRef}>
            <div className="dialog-content">
                <div className="text-wrap">
                    {t('@Configure access permissions for shared folders and spaces.@')}
                </div>

                <TableComponent
                    table_head_fields={table_head_fields}
                    show_checkbox={true}
                    row_component={ManageShareAccessRowComponent}
                    table_data={state}
                    state={state}
                    total_record={0}
                    doRefresh={() => {
                    }}
                    disPatcher={dispatch}
                    table_ui={{table_field_checkbox: false}}
                    search={false}
                    isOwner={true}
                    createText={null}
                    permissions={null}
                    data_table_name="revoke-org-user-space-table"
                    css_with_context_wrapper={false}
                    pagination={false}
                    table_height="400px"
                    empty_data={<ManageShareAccessEmptyList t={t}/>}
                />
                <div className="text-wrap note-msg mt-16">
                    {t('@Notes: If this user inherits access through a group, you cannot change it to No Access.@')}
                </div>
            </div>


        </ModalComponent>
    );
}

export default AccountListManageShareAccess;